/**
 * Read-only registry for the Admin → Automations page.
 * Update this file when adding or changing status writers / reactions.
 */

/** Set true when container unloading workflow is in active use */
export const SHOW_UNLOADING_STATUS_AUTOMATIONS = false;

/** Stable id for DB logs and edge function (booking_automation_runs.automation_id) */
export const LEANCUSTOMS_UTB_TTW_AUTOMATION_ID =
  "leancustoms-utb-ttw-customs-cleared";

export type BookingStatusField = "status" | "unloading_status";

export type StatusUpdaterActor =
  | "customer"
  | "admin"
  | "warehouse"
  | "system";

export interface StatusUpdater {
  id: string;
  label: string;
  field: BookingStatusField;
  /** Human-readable result, e.g. "→ approved" or "→ mapped from container" */
  setsTo: string;
  actor: StatusUpdaterActor;
  trigger: string;
  location: string;
  /** Side effects that run in the same flow (not separate "on land" automations) */
  alsoDoes?: string[];
  notes?: string;
  /** Hidden on Automations page while SHOW_UNLOADING_STATUS_AUTOMATIONS is false */
  requiresUnloadingWorkflow?: boolean;
}

export interface StatusReaction {
  id: string;
  field: BookingStatusField;
  /** Status value or "*" for any transition */
  whenStatus: string;
  label: string;
  kind: "database" | "app" | "planned";
  description: string;
  location?: string;
}

/** Active app/cron automations that change booking state */
export interface AppAutomation {
  id: string;
  /** Same value stored in booking_automation_runs.automation_id when logged */
  automationId: string;
  label: string;
  trigger: string;
  condition: string;
  action: string;
  sideEffects?: string[];
  edgeFunction?: string;
  schedule?: string;
}

export const APP_AUTOMATIONS: AppAutomation[] = [
  {
    id: "leancustoms-utb-ttw-customs-cleared",
    automationId: LEANCUSTOMS_UTB_TTW_AUTOMATION_ID,
    label: "LeanCustoms UTB + TTW → customs cleared",
    trigger: "Scheduled cron (~every 15 minutes)",
    condition:
      "booking.status is customs_pending_clearance or custom_clearance; declaration resolved (saved or auto-linked via LeanCustoms reference search); linked declaration has both UTB and TTW documents",
    action:
      "Set booking.status to customs_cleared when UTB + TTW are present (cron or Run check now)",
    sideEffects: [
      "Existing DB status email notifications for customs_cleared",
    ],
    edgeFunction: "leancustoms-customs-cleared-sync",
    schedule: "pg_cron every 15 min (see function README)",
  },
];

export function getAppAutomationLabel(automationId: string): string {
  const match = APP_AUTOMATIONS.find(
    (a) => a.automationId === automationId || a.id === automationId
  );
  return match?.label ?? automationId;
}

/** Who can write booking status fields today */
export const STATUS_UPDATERS: StatusUpdater[] = [
  {
    id: "wizard-submit",
    label: "Customer submits booking wizard",
    field: "status",
    setsTo: "approved",
    actor: "customer",
    trigger: "Final step confirm on Review & Submit",
    location: "src/components/BookingWizard/steps/ReviewDetails.tsx",
    alsoDoes: ["Assigns first available agent", "Generates product shipping labels"],
    notes: "Goes straight to approved (not submitted).",
  },
  {
    id: "admin-approve",
    label: "Admin approves booking",
    field: "status",
    setsTo: "approved",
    actor: "admin",
    trigger: "Approve button in booking details",
    location: "src/components/BookingsAdmin/BookingDetails.tsx",
  },
  {
    id: "admin-reject",
    label: "Admin rejects booking",
    field: "status",
    setsTo: "rejected",
    actor: "admin",
    trigger: "Reject button in booking details",
    location: "src/components/BookingsAdmin/BookingDetails.tsx",
  },
  {
    id: "admin-manual-save",
    label: "Admin edits booking (General tab)",
    field: "status",
    setsTo: "Any valid status (manual pick)",
    actor: "admin",
    trigger: "Save on General tab in booking details",
    location: "src/components/BookingsAdmin/BookingDetails.tsx",
  },
  {
    id: "admin-bulk-status",
    label: "Admin bulk status change",
    field: "status",
    setsTo: "Selected status",
    actor: "admin",
    trigger: "Bulk action in admin bookings list",
    location: "src/components/BookingsAdmin/BookingsList.tsx",
  },
  {
    id: "sendcloud-shipment-admin",
    label: "Sendcloud shipment created (admin booking)",
    field: "status",
    setsTo: "delivery_planned",
    actor: "admin",
    trigger: "Successful shipment creation in Delivery tab",
    location: "src/components/BookingsAdmin/BookingDetails.tsx",
  },
  {
    id: "sendcloud-shipment-unloading",
    label: "Sendcloud shipment created (container unloading)",
    field: "status",
    setsTo: "delivery_planned",
    actor: "warehouse",
    trigger: "Successful shipment creation from unloading Ship flow",
    location: "src/pages/ContainerUnloadingPage.tsx",
    requiresUnloadingWorkflow: true,
  },
  {
    id: "container-status-single",
    label: "Container status updated (single container)",
    field: "status",
    setsTo: "Mapped: customs → customs_pending_clearance; planned/departed/arrived → in_transit; else same as container",
    actor: "admin",
    trigger: "Change container status on container details page",
    location: "src/components/containers/ContainerDetails.tsx",
    notes: "Updates all bookings in that container.",
  },
  {
    id: "container-status-bulk",
    label: "Bulk container status update",
    field: "status",
    setsTo: "Same mapping as single container update",
    actor: "admin",
    trigger: "Bulk status on containers list",
    location: "src/components/containers/ContainersList.tsx",
    notes: "Updates all bookings in selected containers.",
  },
  {
    id: "master-container-bulk",
    label: "Master container (B/L) bulk update",
    field: "status",
    setsTo: "Mapped from container status, or explicit booking status if set",
    actor: "admin",
    trigger: "Bulk update on master containers list",
    location: "src/components/containers/MasterContainersList.tsx",
    notes: "Updates all bookings under containers in that master B/L.",
  },
  {
    id: "leancustoms-utb-ttw-cron",
    label: "LeanCustoms UTB + TTW documents (scheduled)",
    field: "status",
    setsTo: "customs_cleared",
    actor: "system",
    trigger: "Cron: leancustoms-customs-cleared-sync (~every 15 min)",
    location: "supabase/functions/leancustoms-customs-cleared-sync/index.ts",
    notes:
      "Only from customs_pending_clearance or custom_clearance when linked declaration has both UTB and TTW.",
    alsoDoes: ["Triggers customs_cleared status email notifications (DB)"],
  },
  {
    id: "unloading-finish-count",
    label: "Warehouse finishes carton counting",
    field: "unloading_status",
    setsTo: "delivery_ready",
    actor: "warehouse",
    trigger: "Finish booking after all products counted",
    location: "src/hooks/useProductUpdates.ts",
    alsoDoes: [
      "For delivery bookings: updates booking_delivery cartons/pallets",
    ],
  },
  {
    id: "unloading-po-created",
    label: "Purchase order created (container unloading)",
    field: "unloading_status",
    setsTo: "completed",
    actor: "warehouse",
    trigger: "Successful PO creation in container unloading PO modal",
    location: "src/components/containers/PurchaseOrderManagement.tsx",
    notes: "Admin booking PO tab does not set unloading_status.",
  },
  {
    id: "unloading-no-po",
    label: "Mark stored without PO",
    field: "unloading_status",
    setsTo: "completed",
    actor: "warehouse",
    trigger: "Manual button on storage booking in container unloading",
    location: "src/pages/ContainerUnloadingPage.tsx",
  },
  {
    id: "unloading-manual-form",
    label: "Manual unloading status edit",
    field: "unloading_status",
    setsTo: "pending | in_progress | completed",
    actor: "admin",
    trigger: "Save on container unloading form",
    location: "src/components/containers/ContainerUnloading.tsx",
  },
  {
    id: "unloading-reset",
    label: "Reset container unloading",
    field: "unloading_status",
    setsTo: "cleared (null) → shown as pending",
    actor: "admin",
    trigger: "Reset container on unloading page",
    location: "src/hooks/useContainerUnloading.ts",
    alsoDoes: ["Clears carton/pallet totals and product actual_cartons"],
  },
];

/** Updaters shown on the Automations admin page */
export function getVisibleStatusUpdaters(): StatusUpdater[] {
  if (SHOW_UNLOADING_STATUS_AUTOMATIONS) {
    return STATUS_UPDATERS;
  }
  return STATUS_UPDATERS.filter(
    (u) => u.field === "status" && !u.requiresUnloadingWorkflow
  );
}

/**
 * What runs when a booking reaches a status (or when status changes).
 * App automations: none configured yet — add entries with kind: "app" here.
 */
export const STATUS_REACTIONS: StatusReaction[] = [
  {
    id: "email-n8n-submitted",
    field: "status",
    whenStatus: "submitted",
    label: "n8n customer email flow",
    kind: "database",
    description:
      "DB trigger enqueues email_notifications for submitted (legacy status; wizard now skips this).",
    location: "supabase/migrations — enqueue email on bookings UPDATE",
  },
  {
    id: "email-n8n-approved",
    field: "status",
    whenStatus: "approved",
    label: "n8n customer email flow",
    kind: "database",
    description:
      "DB trigger enqueues email_notifications when status becomes approved (includes wizard submit).",
    location: "supabase/migrations — enqueue email on bookings UPDATE",
  },
  {
    id: "email-n8n-rejected",
    field: "status",
    whenStatus: "rejected",
    label: "n8n customer email flow",
    kind: "database",
    description: "DB trigger enqueues email_notifications when status becomes rejected.",
    location: "supabase/migrations — enqueue email on bookings UPDATE",
  },
  {
    id: "email-in-app-other",
    field: "status",
    whenStatus: "* (except submitted, approved, rejected)",
    label: "In-app status email notifications",
    kind: "database",
    description:
      "DB trigger inserts booking_email_notifications for opted-in users. fyco and delivery_planned are on by default; others opt-in.",
    location:
      "supabase/migrations/20250203000001_exclude_info_email_from_notifications.sql",
  },
];

export const UNLOADING_STATUS_VALUES = [
  "pending",
  "in_progress",
  "delivery_ready",
  "completed",
] as const;

export const ACTOR_LABELS: Record<StatusUpdaterActor, string> = {
  customer: "Customer",
  admin: "Admin",
  warehouse: "Warehouse",
  system: "System",
};

export const ACTOR_COLORS: Record<StatusUpdaterActor, string> = {
  customer: "blue",
  admin: "purple",
  warehouse: "orange",
  system: "cyan",
};

export const REACTION_KIND_LABELS: Record<StatusReaction["kind"], string> = {
  database: "Database trigger",
  app: "App automation",
  planned: "Planned",
};

export const REACTION_KIND_COLORS: Record<StatusReaction["kind"], string> = {
  database: "geekblue",
  app: "green",
  planned: "default",
};
